// NodeMoneyField.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
  formatCurrencyDollarsString,
  formatPrice,
  isCurrencyInputPartial,
  parseCurrencyInputToCents,
  parsePriceCents,
  resolveFieldCurrencyCode,
} from 'utilities/Currency';

// CSS
import './NodeMoneyField.css';

// Components
import TextInput from 'components/textinput/TextInput';

/**
 * NodeMoneyField
 *
 * Displays an inline money amount input for schema-driven fields with save-on-blur.
 * In readOnly / previewMode (visitor Peek): display-only formatted amount.
 *
 * @param {*} value - Current field value (minor units).
 * @param {object} [field] - Schema field definition (uses currencyCode).
 * @param {Function} onChange - Callback when new value is saved.
 * @param {boolean} [readOnly=false] - Display-only.
 * @param {boolean} [previewMode=false] - Alias for readOnly (NodeField visitor path).
 *
 * @returns {ReactNode} - NodeMoneyField.
 */
const NodeMoneyField = ({
  value,
  field = null,
  onChange,
  readOnly = false,
  previewMode = false,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const isReadOnly = Boolean(readOnly || previewMode);

  // ----- STATE ----- //

  const [draftValue, setDraftValue] = useState('');

  // ----- MEMOS ----- //

  const currencyCode = useMemo(() => resolveFieldCurrencyCode(field), [field]);
  const displayValue = useMemo(
    () => formatCurrencyDollarsString(value, currencyCode),
    [currencyCode, value]
  );
  const readOnlyDisplay = useMemo(
    () => {
      if (value === null || value === undefined || value === '') return '';
      // Normalize via parsePriceCents so major-unit strings (e.g. "30.00" from
      // coerceToInput) are not treated as literal cent amounts by formatPrice.
      const cents = parsePriceCents(value, currencyCode);
      if (!Number.isFinite(cents)) return '';
      return formatPrice(cents, currencyCode);
    },
    [currencyCode, value]
  );

  // ----- EFFECTS ----- //

  useEffect(() => {
    setDraftValue(displayValue);
  }, [displayValue]);

  // ----- HANDLERS ----- //

  const handleBlur = useCallback(() => {
    if (isReadOnly || typeof onChange !== 'function') return;

    const nextText = String(draftValue ?? '').trim();
    const currentIsEmpty = value === null || value === undefined || value === '';

    if (nextText === '') {
      if (!currentIsEmpty) {
        onChange('');
      }
      return;
    }

    const parsed = parseCurrencyInputToCents(nextText, currencyCode);
    if (!parsed.ok) {
      setDraftValue(displayValue);
      return;
    }

    const currentCents = currentIsEmpty
      ? ''
      : parseCurrencyInputToCents(displayValue, currencyCode).cents;
    if (parsed.cents !== currentCents) {
      onChange(nextText);
    }
  }, [currencyCode, displayValue, draftValue, isReadOnly, onChange, value]);

  const handleChange = useCallback((event) => {
    if (isReadOnly) return;
    const nextValue = event.target.value;
    if (nextValue === '') {
      setDraftValue('');
      return;
    }

    if (isCurrencyInputPartial(nextValue, currencyCode)) {
      setDraftValue(nextValue);
    }
  }, [currencyCode, isReadOnly]);

  // ----- RENDER ----- //

  if (isReadOnly) {
    return (
      <div className="node-money-field-container">
        <TextInput
          value={readOnlyDisplay}
          placeholder=""
          startIconKey="attach_money"
          startIconColor={theme.foregroundMuted}
          iconSize={22}
          hybrid
          readOnly
        />
      </div>
    );
  }

  return (
    <div className="node-money-field-container">
      <TextInput
        value={draftValue}
        placeholder="Set amount..."
        inputMode="decimal"
        startIconKey="attach_money"
        startIconColor={theme.foregroundMuted}
        iconSize={22}
        hybrid
        onBlur={handleBlur}
        onChange={handleChange}
      />
    </div>
  );
};

NodeMoneyField.displayName = 'NodeMoneyField';

export default NodeMoneyField;
